// Lib
import chroma from 'chroma-js';
import { flow, isUndefined, trim } from 'lodash/fp';

// Utils
import {
    convertToColorSpace,
    getColorObjectCssValue,
    getColorObjectDisplayValue,
    getColorObjectFromText,
    getColorObjectSpace,
    parseColorObject,
} from './colorObjectUtil';
import { isHexColorFormat, isValidColorDisplayFormat, isValidColorFormat } from './colorSpaceUtil';
import { asObject, prop } from '../utils/immutableHelper';

// Constants
import { BACKGROUND_COLORS, COLOR_SPACE, COLORS, SKETCH_BACKGROUND_COLORS } from './colorConstants';

export const getPredefinedColor = (colorName) => COLORS[colorName];
export const getPredefinedBackgroundColor = (colorName) =>
    BACKGROUND_COLORS[colorName] || SKETCH_BACKGROUND_COLORS[colorName];

/**
 * This is used for activity digest emails and custom color pickers, where CSS variables cannot be used.
 * Otherwise getColorCssValue is the better function.
 */
export const getColorDefaultHexValue = (color) => prop('value', parseColorObject(color, COLOR_SPACE.HEX));

/**
 * If the colour is a predefined colour, return the CSS variable string.
 * If the color is a valid color string format or a color object, return CSS color format.
 *
 * e.g. "BLUE" will return "var(--color-element-blue)"
 */
export const getColorCssValue = (color) => {
    const colorSpace = getColorObjectSpace(color);
    if (colorSpace) return getColorObjectCssValue(color);

    if (isValidColorFormat(color)) return color;

    const colorMapEntry = COLORS[color] || BACKGROUND_COLORS[color] || SKETCH_BACKGROUND_COLORS[color];
    if (colorMapEntry) return colorMapEntry.css;

    return null;
};

export const getColorDisplayValue = (color) => {
    const colorSpace = getColorObjectSpace(color);
    if (colorSpace) return getColorObjectDisplayValue(color);

    if (isValidColorDisplayFormat(color)) return color;
    if (isValidColorFormat(color)) {
        return flow(parseColorObject, getColorObjectDisplayValue)(color);
    }

    const colorMapEntry = COLORS[color] || BACKGROUND_COLORS[color] || SKETCH_BACKGROUND_COLORS[color];
    if (colorMapEntry) return colorMapEntry.hex;

    return null;
};

export const getColorCustomPropertyHex = (customPropertyVal, domNode = document.body) => {
    if (!customPropertyVal || !domNode) return null;

    // Strip the "var(" and ")"
    const customPropertyName = customPropertyVal.startsWith('var(')
        ? customPropertyVal.substring(4, customPropertyVal.length - 1)
        : customPropertyVal;

    const hex = getComputedStyle(domNode).getPropertyValue(customPropertyName);

    return trim(hex);
};

/**
 * Returns the HEX code of a predefined colour, by using the DOM to get the actual css custom property value.
 */
export const getColorVariableValue = (color) => {
    if (!getPredefinedColor(color)) return null;

    const colorCustomPropertyString = getColorCssValue(color);

    if (!colorCustomPropertyString) return null;

    return getColorCustomPropertyHex(colorCustomPropertyString);
};

// First use the color name if it's set (e.g. "RED" or "BLUE")
// Otherwise use the value that's passed in, which should be a hex value
export const getColorToPersist = (colorValue) =>
    colorValue && !isUndefined(colorValue.name) ? colorValue.name || null : colorValue || null;

export const getIsStandardColor = (color) => !color || !!COLORS[color];

export const getColorIntensity = (color) => {
    if (!color) return null;

    let colorObject = typeof color === 'string' ? getColorObjectFromText(color) : asObject(color);

    // Ensure that the colour space is always either HEX or RGB so the same calculation is used
    // to determine the intensity, otherwise the same colour in different colour spaces might
    // result in different intensity values
    if (colorObject?.space === COLOR_SPACE.HSL) {
        colorObject = convertToColorSpace(colorObject, COLOR_SPACE.RGB);
    }

    switch (colorObject?.space) {
        case COLOR_SPACE.HEX:
        case COLOR_SPACE.RGB: {
            // This is based off a stackoverflow answer: https://stackoverflow.com/questions/3942878
            // There is a more advanced formula if required.
            const { r, g, b } = colorObject;

            return r * 0.299 + g * 0.587 + b * 0.114;
        }
        default:
            return null;
    }
};

export const getIsLightColor = (color, sensitivity = 130) => {
    const intensity = getColorIntensity(color);

    if (intensity === null) return true;

    return intensity > sensitivity;
};

export const getOverlayColorForColor = (color, isDarkMode) => {
    const colorIntensity = getColorIntensity(color);

    const alpha = ((250 - colorIntensity) / 230) * 0.92 || 0.92;
    return isDarkMode ? `rgba(0, 0, 0, ${1 - alpha})` : `rgba(255, 255, 255, ${alpha})`;
};

/**
 * Based on an input colour - whether it's hex or a predefined colour like YELLOW,
 * this returns the CSS property that should be used by text that's sitting on top of it.
 * This is used in line labels, colorSwatches, as well as comment thread pins.
 */
export const getCssPropertyForTextOnColor = (color, sensitivity) => {
    if (!color) return 'var(--ui-text-color-match-canvas)';

    if (getColorObjectSpace(color) || isValidColorFormat(color)) {
        return getIsLightColor(color, sensitivity) ? 'var(--ui-text-color-dark)' : 'var(--ui-text-color-light)';
    }

    switch (color) {
        case COLORS.YELLOW.name:
            return 'var(--ui-text-color-dark)';
        default:
            // Depends on dark mode / light mode
            return 'var(--ui-text-color-match-canvas)';
    }
};

export const getCssPropertyForTextOnPinColor = (color) => {
    if (!color) return 'var(--ui-text-color-match-canvas)';

    if (isHexColorFormat(color)) {
        return getIsLightColor(color) ? 'var(--ui-text-color-dark)' : 'var(--ui-text-color-light)';
    }

    switch (color) {
        // Depends on dark mode / light mode
        case COLORS.GREY.name:
        case COLORS.CYAN.name:
        case COLORS.YELLOW.name:
            return 'var(--ui-text-color-dark)';
        default:
            return 'var(--ui-text-color-match-canvas)';
    }
};

export const getIsLowContrastDefaultColorCss = ({ color, isDarkModeTheme }) => {
    switch (color) {
        case SKETCH_BACKGROUND_COLORS.WHITE.css:
            return !isDarkModeTheme;
        default:
            return false;
    }
};

export const getIsLowContrastColorSwatch = ({ color, isDarkModeTheme }) => {
    if (!isHexColorFormat(color)) return getIsLowContrastDefaultColorCss({ color, isDarkModeTheme });

    const comparisonColor = isDarkModeTheme ? '#3d4554' : '#ffffff';

    const contrast = chroma.contrast(comparisonColor, color);
    // harder to tell dark shades apart than light shades
    const minimumContrast = isDarkModeTheme ? 1.6 : 1.2;

    return contrast <= minimumContrast;
};

export const getColorIsColorful = (colorValue) => {
    try {
        const color = chroma(colorValue);

        return color.get('hsl.s') > 0.3 && color.get('hsl.l') > 0.2;
    } catch (error) {
        return false;
    }
};

/**
 * Returns true if colorValue is darker than the comparison color.
 */
export const getIsColorDarkerThan = (colorValue, comparisonColorValue) =>
    chroma(colorValue).luminance() < chroma(comparisonColorValue).luminance();

/**
 * Returns a color that is a mix of the base color and the overlay color.
 * Great for when you need it to look like there is one color with an opacity over another color.
 */
export const mixColorsForOverlayEffect = (baseColor, overlayColor, mixAmount = 0.1) =>
    chroma.mix(baseColor, overlayColor, mixAmount, 'rgb').toString();
